import { useEffect, useState } from "react";
import { Bot, Globe, Megaphone } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ResourceDraft } from "../ResourceWizard";
import { ChoiceCard, FieldLabel } from "./WizardUi";

type Props = {
  draft: ResourceDraft;
  onNext: (patch: Partial<ResourceDraft>) => Promise<void>;
};

type ResourceKind = "channel_chat" | "bot" | "no_check_resource";

const TYPES = [
  { value: "channel_chat", label: "канал/чат", icon: Megaphone },
  { value: "bot", label: "бот", icon: Bot },
  { value: "no_check_resource", label: "ресурс (без проверки)", icon: Globe },
] satisfies Array<{ value: ResourceKind; label: string; icon: LucideIcon }>;

export function Step1Type({ draft, onNext }: Props) {
  const [name, setName] = useState(draft.name);
  const [kind, setKind] = useState<ResourceKind>(kindFromDraft(draft));
  const valid = name.trim().length > 0;

  useEffect(() => {
    setName(draft.name);
    setKind(kindFromDraft(draft));
  }, [draft.name, draft.type, draft.verification_mode]);

  return (
    <div className="tp-resource-wizard-step space-y-3">
      <div className="space-y-2">
        <FieldLabel>укажите название</FieldLabel>
        <input
          className="tp-input"
          placeholder="название ресурса"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <FieldLabel>тип ресурса</FieldLabel>
        <div className="grid gap-2">
          {TYPES.map((t) => (
            <ChoiceCard
              key={t.value}
              label={t.label}
              icon={t.icon}
              active={kind === t.value}
              onClick={() => setKind(t.value)}
            />
          ))}
        </div>
      </div>
      <button
        disabled={!valid}
        className="tp-button-primary w-full disabled:opacity-50"
        onClick={() => onNext(patchForKind(name, kind))}
      >
        далее
      </button>
    </div>
  );
}

function kindFromDraft(draft: ResourceDraft): ResourceKind {
  if (draft.type === "bot") return "bot";
  if (draft.verification_mode === "no_check") return "no_check_resource";
  return "channel_chat";
}

function patchForKind(
  name: string,
  kind: ResourceKind,
): Partial<ResourceDraft> {
  if (kind === "bot") {
    return {
      name,
      type: "bot",
      target_action: "start_bot",
      verification_mode: "verified",
      bot_verification_method: "botmembers",
      hold_enabled: true,
    };
  }
  if (kind === "no_check_resource") {
    return {
      name,
      type: "channel_public",
      target_action: "subscribe",
      verification_mode: "no_check",
      hold_enabled: false,
    };
  }
  return {
    name,
    type: "channel_public",
    target_action: "subscribe",
    verification_mode: "verified",
    hold_enabled: true,
  };
}
